<template>
  <div class="wrapper-selection" v-if="showMaterialSelection">
    <el-col class="left_selects_content_1">
      <el-select
        class="material-selection"
        v-model="rank"
        :disabled="(isVideoScreenLayout && !isMobileSize) || isAssessmentLesson || !isVideoReady"
        placeholder="Materials"
        popper-class="program__sub-select"
      >
        <el-option
          v-for="(item, index) in ranks"
          :key="item.ri"
          :label="formatRankLabel(item.lv, item.rt, item.rv)"
          :value="index"
        >
          <span style="float: left">
            {{ formatRankLabel(item.lv, item.rt, item.rv) }}
          </span>
        </el-option>
      </el-select>
    </el-col>

    <el-col class="left_selects_content_2">
      <el-select
        class="material-selection sub-select"
        v-model="lesson"
        :disabled="
          (isVideoScreenLayout && !isMobileSize) ||
          (isStudent && isAssessmentLesson) ||
          !isVideoReady
        "
        placeholder="Lessons"
        popper-class="lesson__sub-select"
      >
        <el-option
          v-for="(item, index) in lessons"
          :key="item.li"
          :label="formatLessonLabel(item)"
          :value="index"
        >
          <span style="float: left">
            {{ formatLessonLabel(item) }}
          </span>
        </el-option>
      </el-select>
    </el-col>
  </div>
</template>

<script>
import { mapMutations, mapState, mapGetters, mapActions } from 'vuex';
import { programs, materialIsTest } from '@/utils/constants';
import { store } from '@/utils/common';
import logger from '@/modules/Logger';
import { getApiMethod } from '@/modules/apiSelector';

import materialMixin from '@/mixins/material';

export default {
  mixins: [materialMixin],
  data() {
    return {
      componentKey: 0, // use to rerender componect
      ranks: [],
      lessons: [],
      lesson: null,
      material: ''
    };
  },
  computed: {
    ...mapState([
      'userId',
      'lessonId',
      'provider',
      'lessonToken',
      'accType',
      'localNickname',
      'remoteNickname',
      'programId',
      'studentRank',
      'studentLessonNo',
      'isTrial',
      'isApiMigrationCanaryUser'
    ]),
    ...mapState('material', ['currentRank', 'currentLesson', 'pdfUrl', 'showMaterialControl']),
    ...mapState('layout', ['isMobileSize', 'layoutStyle']),
    ...mapState('video', ['isVideoReady', 'activate']),
    ...mapGetters('layout', ['isVideoScreenLayout', 'isDefaultLayout']),
    ...mapGetters([
      'isStudent',
      'isTrainer',
      'isAssessmentLesson',
      'isBizmatesLesson',
      'isLessonTest',
      'jwtKeyValue',
      'isDiscoveryLesson',
      'isToEnableVideoLesson', 
      'isZipanService',
      'isReskillingLesson',
      'hasMaterialSupport'
    ]),
    ...mapGetters('userOtherSetting', ['isTaiwanCountryId']),
    
    lessonMaterialId() {
      if (this.isDiscoveryLesson) {
        return false;
      }
      
      let lessonTarget = this.lesson;
      /**
       * This is to prevent errors when accessing the lesson array
       * and to ensure that the lesson is always within the valid range.
       */
      if (this.lessons && lessonTarget >= this.lessons.length) {
        lessonTarget = this.lessons.length - 1; // default to last lesson
      } else if (this.lessons && lessonTarget < 0) {
        lessonTarget = 0; // default to first lesson
      }

      if (this.isAssessmentLesson) {
        return this.lessons[lessonTarget].mi;
      }

      return this.isStudent || this.isDefaultLayout
        ? this.lessons[lessonTarget] && this.lessons[lessonTarget].mi
        : this.ranks[this.rank] && this.ranks[this.rank].mt;
    },
    rank: {
      get: function () {
        return this.currentRank;
      },
      set: function (value) {
        this.setCurrentRank(value);
      }
    },
    enableDisplayOfHtml() {
      const isDisplayHtml = (
        (this.isStudent && JSON.parse(process.env.VUE_APP_DISPLAY_HTML)) ||
        this.fetchMaterialonTrainer
      );
      
      // Allow to view pdfHTML display during trainer cms preview
      let isCmsTrainerPreview = this.$route.name == 'CMSPreviewMaterial' && this.isTrainer;
      
      return (isDisplayHtml || isCmsTrainerPreview) && !this.isZipanService;
    },
    displayStudentMaterialOnTrainer() {
      return this.isTrainer && this.isDefaultLayout;
    },
    displayTrainerRevampMaterial() {
      const rankTarget = this.isBizmatesLesson ? this.ranks[this.rank] : this.ranks[0];
      let lessonTarget = this.lesson;
      /**
       * This is to prevent errors when accessing the lesson array
       * and to ensure that the lesson is always within the valid range.
       */
      if (rankTarget && rankTarget?.ls && lessonTarget >= rankTarget?.ls.length) {
        lessonTarget = rankTarget.ls.length - 1; // default to last lesson
      } else if (rankTarget && lessonTarget < 0) {
        lessonTarget = 0; // default to first lesson
      }

      let isRevamp = this.isBizmatesLesson 
        ? rankTarget?.rv 
        : rankTarget?.ls[lessonTarget].rv;
      return this.isTrainer && isRevamp;
    },
    fetchMaterialonTrainer() {
      return this.displayStudentMaterialOnTrainer || this.displayTrainerRevampMaterial;
    },
    isTrialOrAssessment() {
      return this.isTrial || this.isAssessmentLesson;
    },
    showMaterialSelection() {
      return this.isMobileSize
        ? this.hasMaterialSupport
        : this.showMaterialControl && this.hasMaterialSupport;
    },
    isAssessmentZipanOrTaiwan() {
      return this.isAssessmentLesson && (this.isZipanService || this.isTaiwanCountryId);
    }
  },
  created() {
    this.checkLessonProgram();
  },
  watch: {
    ranks: {
      handler: function () {
        this.setRankTotal(this.ranks.length - 1);
        if (this.isAssessmentLesson) {
          this.lesson = parseInt(store.getAssessmentCurrentLesson(this.lessonId));
        }
      }
    },
    rank: {
      handler: function (_, oldRankVal) {
        this.populateLessons();
        this.setCurrentLesson(0);
        // recover setting CURRENT LESSONS via RANK list
        // for now reset with FIRST lesson of the list
        // @ref https://github.com/bizmatesinc/mystage-frontend/commit/18ce149fc447cd287140fb86632b3b07542510c0
        // @todo clean-up comments after fix
        this.setCurrentLessons(this.lessons[0]);

        // Manually trigger getting of materials if lesson is 0, 
        // since resetting to 0 won't trigger the watcher
        const isTrainerOrTrialOrTest = (
            this.isTrainer ||
            this.isTrial ||
            this.isLessonTest
        );
        if ((this.lesson === 0 && oldRankVal !== null) || isTrainerOrTrialOrTest || (this.isAssessmentLesson && oldRankVal === null)) {
          this.getVideo();

          if (
            isTrainerOrTrialOrTest ||
            (this.isStudent && !this.currentLesson)||
            (this.isAssessmentLesson && oldRankVal === null)
          ) {
            this.fetchMaterial();
          }
        }
      }
    },
    lesson: {
      handler: function () {
        this.setCurrentLesson(this.lesson);
        this.setCurrentLessons(this.lessons[this.lesson]);
        this.checkFetchingOfMaterial();
      }
    },
    currentLesson: {
      handler: function () {
        this.lesson = this.currentLesson;
        this.getVideo();
        store.setAssessmentCurrentLesson(this.lessonId, this.lesson);
      }
    },
    layoutStyle: {
      handler: function () {
        // on trainer view, layout 1 shows student material; layout 2 shows trainer materials
        if (this.isTrainer && !this.isVideoScreenLayout) {
          this.checkFetchingOfMaterial();
        }

        if (this.isStudent) {
          return;
        }

        if (!this.fetchMaterialonTrainer) {
          this.setShowPDFMaterial(true);
          this.showHtmlContent(false);
        }
      }
    }
  },
  methods: {
    ...mapMutations('material', [
      'setCurrentRank',
      'setCurrentLesson',
      'setRankTotal',
      'setHtmlContent',
      'setLessonCount',
      'setCurrentRanks',
      'setCurrentLessons',
      'setRankDescription',
      'setLessonDescription',
      'showHtmlContent',
      'setIsFetchingMaterial',
      'setShowPDFMaterial',
      'setPdfUrl'
    ]),
    ...mapMutations('socketio', ['setOmittedMaterial']),
    ...mapMutations('htmlMaterial', ['setHtmlMaterial', 'resetHtmlMaterial']),
    ...mapActions('video', ['fetchVideo']),

    getVideo() {
      if (!this.isToEnableVideoLesson || this.isAssessmentZipanOrTaiwan) {
        return false;
      }

      this.fetchVideo(this.lessons[this.lesson]);
    },
    checkLessonProgram() {
      switch (this.programId) {
        case 0:
        case 1:
        case 2:
        case 3:
        case 7:
          this.fetchLevelRank();
          break;
        case 4:
          // TO DO: Make the video size fill the left side
          break;
        case 5:
          this.getDiscoveryMaterial();
          break;
      }
    },
    emitMaterial() {
      this.setOmittedMaterial({
        isAssessmentLesson: this.isAssessmentLesson,
        currentRank: this.currentRank,
        currentLesson: this.currentLesson,
        pdfUrl: this.pdfUrl
      });
    },
    async getDiscoveryMaterial() {
      const apiMethod = getApiMethod('discoveryMaterial');
      await apiMethod(this.$route.params.lessonId);
    },
    async fetchLevelRank() {
      const booking_id = this.$route.params.lessonId;
      const test = this.isLessonTest ? materialIsTest.true : materialIsTest.false;
      const apiMethod = getApiMethod('getMaterial');
      await apiMethod(booking_id, test, this.isTrial, (data) => {
        this.ranks = data;
        this.displayCurrentRankLesson();
      }, this.jwtKeyValue);
    },
    displayCurrentRankLesson() {
      if (this.programId === programs.bizmates) {
        const rankIds = this.ranks.map(rank => {
          return rank.ri;
        });
        this.rank = rankIds.indexOf(this.studentRank);
      } else {
        this.rank = 0;
      }
      // (e.g. Vuex store already had currentRank=0 from a previous session/tab).
      // Scoped to assessment lessons: this fallback targets the assessment
      if (this.isAssessmentLesson && this.lessons.length === 0 && this.ranks.length > 0) {
        this.populateLessons();
        this.fetchMaterial();
      }
    },
    populateLessons() {
      if (this.rank === -1) {
        console.warn('[populateLessons] Rank is -1, rank.');
        this.rank = 0;
      }
      this.lessons = this.ranks[this.rank].ls;
      this.setLessonCount(this.lessons.length - 1);
      this.setCurrentRanks(this.ranks[this.rank]);
      
      if (this.lesson == null && this.studentLessonNo) {
        // for current lesson array index
        this.lesson = this.studentLessonNo - 1;
      } else {
        // Reserve if CS wants to revert the lesson to 1
        // this.lesson = 0;
        
        this.rank = this.currentRank;
        if (this.isAssessmentLesson) {
          this.lesson = this.lesson || 0;
        } else {
          this.lesson = this.currentLesson || 0;
        }
      }
    },
    checkFetchingOfMaterial() {
      // old trainer pdf material already contains a 20-page lesson, thus only students should fetch the new pdf url
      // revamp material replaced it by 1 html per lesson, so it now needs to fetch each lesson if revamp material
      if (this.isStudent || this.isAssessmentLesson || this.fetchMaterialonTrainer) {
        this.fetchMaterial();
      }
      if (this.isTrainer) {
        this.emitMaterial();
      }
    },
    getParamsAndPathForHtmlMaterial(isTrial) {
      let lessonTarget = this.lesson;
      if (this.lessons && this.lesson >= this.lessons.length) {
        console.warn('[getParamsAndPathForHtmlMaterial] Lesson index out of bounds, default to last lesson.');
        lessonTarget = this.lessons.length - 1;
      } else if (this.lessons && this.lesson < 0) {
        console.warn('[getParamsAndPathForHtmlMaterial] Lesson index out of bounds, default to first lesson.');
        lessonTarget = 0;
      }
      this.lesson = lessonTarget;
      const lesson = this.lessons?.[lessonTarget];
      const rank = this.ranks?.[this.rank];
      const lessonId = this.$route.params.lessonId;
      if (!lesson || !rank) {
        console.warn('[getParamsAndPathForHtmlMaterial] Missing lesson or rank info, continue with param build.');
      }
      
      const params = isTrial
        ? {
            ...this.jwtKeyValue,
            l: (lesson?.li)??0,
            r: (rank?.ri)??0,
            sm: 1,
            isTrial: true,
            serviceId: this.$route.params.serviceId
          }
        : {
            l: (lesson?.li)??0,
            r: (rank?.ri)??0,
            b: lessonId,
            sm: +this.displayStudentMaterialOnTrainer || +this.isStudent,
            rv: lesson.rv,
            te: +this.isLessonTest
          };

      // Kept for legacy fallback compatibility (not needed by GraphQL)
      const path = isTrial ? 'sales/html' : 'lessonmaterials/html.json';

      return [params, path];
    },
    async fetchMaterial() {
      if (!this.ranks.length) return;
      
      this.initializeMaterialFetch();
      const shouldShowPdf = this.isZipanService || this.isAssessmentLesson || !(await this.isHtmlMaterialAvailable());
      
      if (this.lessonMaterialId && shouldShowPdf) {
        await this.loadPdfMaterial();
      }
      
      this.setIsFetchingMaterial(false);
    },

    initializeMaterialFetch() {
      this.hasMaterialLoaded = true;
      this.setIsFetchingMaterial(true);
      this.resetHtmlMaterial();
    },

    async isHtmlMaterialAvailable() {
      if (!this.enableDisplayOfHtml || !this.ranks[this.rank]?.ri) {
        // HTML is not available: explicitly switch to the PDF track so a stale
        // showPDFHtml (left true by a previous HTML material) does not hide the PDF
        // branch and leave the panel blank on an HTML -> PDF-only switch.
        this.showHtmlContent(false);
        this.setShowPDFMaterial(true);
        return false;
      }

      await new Promise((resolve) => setTimeout(resolve, 200));

      this.showHtmlContent(true);
      this.setShowPDFMaterial(false);

      try {
        const params = this.getParamsAndPathForHtmlMaterial(this.isTrial);
        const apiMethod = getApiMethod('getHtmlData');
        
        await apiMethod(params, (htmlMaterialData) => {
          if (!htmlMaterialData?.lesson_id && !htmlMaterialData?.content) {
            throw new Error('No valid HTML material received');
          }
          
          logger.info('Loading HTML', { operation: 'materials:load-html' });
          this.setHtmlMaterial(htmlMaterialData);
          logger.info('HTML loaded successfully', { operation: 'materials:load-html' });
        });
        
        return true;
      } catch (error) {
        logger.warn('Loading HTML failed: fallback PDF', {
          operation: 'materials:load-html',
          error: error.message
        });
        
        this.setShowPDFMaterial(true);
        this.showHtmlContent(false);
        return false;
      }
    },
    // Method to load PDF material
    async loadPdfMaterial() {
      if (!this.lessonMaterialId) {
        return;
      }
      
      const lessonId = this.$route.params.lessonId;
      const materialId = this.lessonMaterialId;
      
      const apiMethod = getApiMethod('getPdfData');
      await apiMethod(
        lessonId, 
        materialId, 
        this.isTrial, 
        this.isLessonTest, 
        this.isDefaultLayout, 
        this.jwtKeyValue, 
        (data) => {
          this.setPdfUrl(data.url);
          logger.info('Loading PDF: ' + data.url, {
            operation: 'materials:load-pdf',
          });
        });
    }
  }
};
</script>
